import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  IjobCard, TjobCardRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../commerce-client';

/**
 * Represents a Job Card in the commerce system, typically used for service or repair jobs.
 * Provides functionality to manage job cards including CRUD operations and filtering.
 *
 * @class JobCard
 * @extends DatabaseAuto
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link JobCard} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const jobCard = await JobCard.getAll();
 *
 * const jobCard = await JobCard.filterAll({
 *
 * });
 *
 * const jobCard = await JobCard.bulkDelete({
 *
 * });
 *
 * const jobCard = await JobCard.getOne('resourceId');
 *
 * const jobCard = await JobCard.remove();
 *
 * ```
 */
export class JobCard extends DatabaseAuto {
  /** Base URL for Job Card API endpoints. @private @static @readonly */
  private static readonly baseUrl = '/job-card';

  /** Unique reference identifier for the job card. @readonly */
  readonly urId: IjobCard['urId'];

  /** Client information associated with the job card. @readonly */
  readonly client: IjobCard['client'];

  /** Machine details related to the job. @readonly */
  readonly machine: IjobCard['machine'];

  /** Description of the problem or service required. @readonly */
  readonly problem: IjobCard['problem'];

  /** Cost associated with the job. @readonly */
  readonly cost: IjobCard['cost'];

  /** Company information related to the job. @readonly */
  readonly company: IjobCard['company'];

  /**
   * Creates a new JobCard instance with the provided data.
   *
   * @constructor
   * @param {IjobCard} data - The job card data used to initialize the instance.
   *
   * @example
   * const jobCard = new JobCard({
   *   urId: 'JC-12345',
   *   client: { name: 'John Doe', contact: 'john@example.com' },
   *   machine: { model: 'XYZ-2000', serial: 'SN12345' },
   *   problem: 'Routine maintenance required',
   *   cost: 199.99,
   *   company: { id: 'comp-123', name: 'ACME Corp' }
   * });
   */
  constructor(data: IjobCard) {
    super(data);
    this.urId = data.urId;
    this.client = data.client;
    this.machine = data.machine;
    this.problem = data.problem;
    this.cost = data.cost;
    this.company = data.company;
  }

  /**
   * Retrieves all job cards with pagination support.
   *
   * @static
   * @async
   * @param {number} [offset=0] - The starting point for pagination.
   * @param {number} [limit=20] - The number of items to retrieve per page.
   * @returns {Promise<{ count: number, dataArr: JobCard[] }>} Object
   * containing total count and array of job card instances.
   * @throws {Error} When the API request fails.
   *
   * @example
   * const { count, dataArr } = await JobCard.getAll(0, 10);
   * console.log(`Found ${count} job cards`);
   */
  static async getAll(offset = 0, limit = 20): Promise<{ count: number; dataArr: JobCard[] }> {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IjobCard>>(`${JobCard.baseUrl}?${query}`);
    const jobCards = await lastValueFrom(observer$);

    return {
      count: jobCards.count,
      dataArr: jobCards.data.map(val => new JobCard(val))
    };
  }


  /**
   * Filters job cards based on the provided criteria.
   *
   * @static
   * @async
   * @param {IfilterProps} filter - The filter criteria for retrieving job cards.
   * @returns {Promise<{ count: number, dataArr: JobCard[] }>} Object
   * containing total count and filtered job card instances.
   * @throws {Error} When the filter operation fails.
   *
   * @example
   * const filter = { 'client.name': 'John Doe', 'status': 'in-progress' };
   * const { count, dataArr } = await JobCard.filterAll(filter);
   */
  static async filterAll(filter: IfilterProps): Promise<{ count: number; dataArr: JobCard[] }> {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IjobCard>>(`${JobCard.baseUrl}/filter`, filter);
    const jobCards = await lastValueFrom(observer$);

    return {
      count: jobCards.count,
      dataArr: jobCards.data.map(val => new JobCard(val))
    };
  }

  /**
   * Retrieves a single job card by its resource ID.
   *
   * @static
   * @async
   * @param {string} resourceId - The unique identifier of the job card to retrieve.
   * @returns {Promise<JobCard>} The requested job card instance.
   * @throws {Error} When the job card is not found or request fails.
   *
   * @example
   * const jobCard = await JobCard.getOne('job-card-123');
   * console.log(jobCard.problem);
   */
  static async getOne(resourceId: string): Promise<JobCard> {
    const observer$ = CommerceClient.ehttp
      .makeGet<IjobCard>(`${JobCard.baseUrl}/${resourceId}`);
    const jobCard = await lastValueFrom(observer$);

    return new JobCard(jobCard);
  }

  /**
   * Creates a new job card entry in the system.
   *
   * @static
   * @async
   * @param {TjobCardRequest<true>} payload - The data for the new job card.
   * @returns {Promise<JobCard>} The newly created job card instance.
   * @throws {Error} When creation fails due to invalid data or server error.
   *
   * @example
   * const newJobCard = await JobCard.add({
   *   client: 'client-123',
   *   machine: 'machine-456',
   *   problem: 'Annual maintenance required',
   *   cost: 249.99,
   *   company: 'company-789'
   * });
   */
  static async add(payload: TjobCardRequest<true>): Promise<JobCard> {
    const observer$ = CommerceClient.ehttp
      .makePost<IjobCard>(`${JobCard.baseUrl}`, payload);

    return new JobCard(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple job cards based on the provided criteria.
   *
   * @static
   * @async
   * @param {IbulkDelete} payload - The criteria for bulk deletion.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk deletion.
   * @throws {Error} When the deletion operation fails.
   *
   * @example
   * const result = await JobCard.bulkDelete({ ids: ['jc-1', 'jc-2'] });
   * console.log(`Deleted ${result.deletedCount} job cards`);
   */
  static bulkDelete(payload: IbulkDelete): Promise<IbulkDeleteSuccessResponse> {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${JobCard.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current job card instance with new data.
   *
   * @async
   * @param {TjobCardRequest} payload - The updated job card data.
   * @returns {Promise<JobCard>} The updated job card instance.
   * @throws {Error} When the update operation fails.
   *
   * @example
   * await jobCard.update({
   *   problem: 'Updated problem description',
   *   cost: 299.99
   * });
   */
  async update(payload: TjobCardRequest): Promise<JobCard> {
    const observer$ = CommerceClient.ehttp
      .makePut<IjobCard>(`${JobCard.baseUrl}/${this._id}`, payload);

    return new JobCard(await lastValueFrom(observer$));
  }

  /**
   * Removes the current job card instance from the system.
   *
   * @async
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion.
   * @throws {Error} When the deletion operation fails.
   *
   * @example
   * const result = await jobCard.remove();
   * if (result?.deletedCount === 1) {
   *   console.log('Job card deleted successfully');
   * }
   */
  remove(): Promise<IdeleteOneResponse | null> {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${JobCard.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
